import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  useTheme,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { localData } from '../services/localData';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import StorageIcon from '@mui/icons-material/Storage';
import InsightsIcon from '@mui/icons-material/Insights';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupIcon from '@mui/icons-material/Group';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpIcon from '@mui/icons-material/Help';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import TableChartIcon from '@mui/icons-material/TableChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import LanguageIcon from '@mui/icons-material/Language';
import { APP_VERSION } from '../version';

const Header = ({ sidebarWidth = 0 }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  // Primary navigation links
  const primaryLinks = [
    { title: 'APH Global', path: '/', icon: LanguageIcon },
    { title: 'Coda', path: '/coda', icon: StorageIcon },
    { title: 'CRM', path: '/crm', icon: GroupIcon },
    { title: 'Campaign', path: '/campaign', icon: CalendarMonthIcon },
    { title: 'Data Projects', path: '/data-projects', icon: FolderIcon },
    { title: 'Upload', path: '/upload', icon: CloudUploadIcon },
  ];

  // Secondary links (in More menu)
  const secondaryLinks = [
    { title: 'Forms', path: '/forms', icon: DynamicFormIcon },
    { title: 'Documentation', path: '/docs', icon: HelpIcon },
  ];

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/projects';
    return location.pathname.startsWith(path);
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'white',
        ml: { md: `${sidebarWidth}px` },
        width: { md: `calc(100% - ${sidebarWidth}px)` },
        transition: 'margin-left 0.2s ease, width 0.2s ease',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 56, md: 64 }, pl: { xs: 7, md: 0 } }}>
          {/* Logo */}
          <Typography
            variant="h6"
            component="div"
            onClick={() => navigate('/')}
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 500,
              color: theme.palette.primary.main,
              letterSpacing: '-0.3px',
              cursor: 'pointer',
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              mr: 3
            }}
          >
            Data Insights
            <Typography
              component="span"
              sx={{
                fontSize: '0.6rem',
                color: 'text.secondary',
                ml: 0.5,
                fontFamily: 'monospace',
                opacity: 0.6,
                verticalAlign: 'super'
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1 }}>
              {primaryLinks.map((link) => {
                const active = isActive(link.path);
                const Icon = link.icon;
                return (
                  <Box key={link.path} sx={{ position: 'relative' }}>
                    <Button
                      color="inherit"
                      onClick={() => navigate(link.path)}
                      startIcon={<Icon sx={{ fontSize: 18 }} />}
                      sx={{
                        fontWeight: active ? 600 : 400,
                        color: active ? theme.palette.primary.main : 'text.primary',
                        fontSize: '0.8rem',
                        py: 1,
                        px: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(0,43,69,0.04)',
                          color: theme.palette.primary.main
                        }
                      }}
                    >
                      {link.title}
                    </Button>
                    {active && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: -1,
                        left: 0,
                        width: '100%',
                        height: '2px',
                        backgroundColor: theme.palette.primary.main
                      }} />
                    )}
                  </Box>
                );
              })}

              {/* More menu */}
              <Button
                color="inherit"
                onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
              >
                More
              </Button>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
              >
                {secondaryLinks.map(link => {
                  const Icon = link.icon;
                  return (
                    <MenuItem
                      key={link.path}
                      onClick={() => { navigate(link.path); setMoreMenuAnchor(null); }}
                      selected={isActive(link.path)}
                    >
                      <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                      <ListItemText>{link.title}</ListItemText>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          )}

          {/* Right side actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Sign out">
              <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Mobile menu */}
            {isMobile && (
              <>
                <IconButton onClick={(e) => setMobileMenuAnchor(e.currentTarget)}>
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={Boolean(mobileMenuAnchor)}
                  onClose={() => setMobileMenuAnchor(null)}
                >
                  {[...primaryLinks, ...secondaryLinks].map(link => {
                    const Icon = link.icon;
                    return (
                      <MenuItem
                        key={link.path}
                        onClick={() => { navigate(link.path); setMobileMenuAnchor(null); }}
                        selected={isActive(link.path)}
                      >
                        <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                        <ListItemText>{link.title}</ListItemText>
                      </MenuItem>
                    );
                  })}
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
